import React, { useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Snackbar,
    Alert,
    FormControlLabel,
} from "@mui/material";
import api from "../../axios/axiosInstance";
import { FloatingGradelyLoader } from "../../loadingIcons/LoadingIcons";

const ManageStudentPublicAccess = () => {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [showPin, setShowPin] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const fetchStudents = async () => {
        setLoading(true);
        try {

            const res = await api.get(`${process.env.REACT_APP_BACKEND_URL}/auth/students/`, {
                headers: {
                    "Content-Type": "application/json",
                },
            });
            const data = Array.isArray(res.data) ? res.data : [];
            const sorted = data.sort((a, b) => a.last_name.localeCompare(b.last_name));

            // Preserve public_pin from previous state
            setStudents((prev) =>
                sorted.map((s) => {
                    const existing = prev.find((p) => p.id === s.id);
                    return existing?.public_pin ? { ...s, public_pin: existing.public_pin } : s;
                })
            );
        } catch (err) {
            console.error("Error fetching students:", err);
            setSnackbar({ open: true, message: "Error fetching students.", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelected(students.map((s) => s.id));
        } else {
            setSelected([]);
        }
    };

    const handleSelect = (id) => {
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
        );
    };

    const handleGenerateCredentials = async () => {

        try {
            const res = await api.post(
                `${process.env.REACT_APP_BACKEND_URL}/auth/students/generate-public-credentials/`,
                { enable_public: true },
                {
                    headers: {
                        "Content-Type": "application/json",

                    },
                }
            );

            const credentials = res.data.credentials || [];

            // Merge new public_pin into state
            setStudents((prev) =>
                prev.map((s) => {
                    const match = credentials.find((c) => c.student_id === s.student_id);
                    return match ? { ...s, public_pin: match.pin } : s;
                })
            );

            let message = `✅ ${res.data.updated} credentials created. ${res.data.skipped} skipped.`;
            if (res.data.cooldown_info) {
                message += ` ${res.data.cooldown_info}`;
            }

            setSnackbar({
                open: true,
                message: message,
                severity: "success",
            });

            // Do not re-fetch students immediately — it would overwrite pins
        } catch (err) {
            console.error("Error generating credentials:", err);
            
            // Handle cooldown error (429 status)
            if (err.response && err.response.status === 429) {
                const errorData = err.response.data;
                setSnackbar({ 
                    open: true, 
                    message: `⏰ ${errorData.error}`, 
                    severity: "warning" 
                });
            } else {
                setSnackbar({ 
                    open: true, 
                    message: "Error generating credentials.", 
                    severity: "error" 
                });
            }
        }
    };

    const handleDeleteCredentials = () => {
        if (selected.length === 0) {
            setSnackbar({ open: true, message: "No students selected.", severity: "warning" });
            return;
        }
        setDialogOpen(true);
    };

    const confirmDelete = async () => {
        try {

            await api.patch(
                `${process.env.REACT_APP_BACKEND_URL}/auth/students/delete-public-credentials/`,
                { student_ids: selected },
                {
                    headers: {
                        "Content-Type": "application/json",

                    },
                }
            );
            setSnackbar({ open: true, message: "Credentials deleted.", severity: "success" });
            setSelected([]);
            fetchStudents();
        } catch (err) {
            setSnackbar({ open: true, message: "Error deleting credentials.", severity: "error" });
        } finally {
            setDialogOpen(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
                <Button variant="contained" color="info" onClick={fetchStudents}>
                    View Student List
                </Button>
                <Button variant="contained" color="success" onClick={handleGenerateCredentials}>
                    Create Credentials
                </Button>
                <Button variant="contained" color="error" onClick={handleDeleteCredentials}>
                    Delete Credentials
                </Button>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showPin}
                            onChange={(e) => setShowPin(e.target.checked)}
                        />
                    }
                    label="Show PIN"
                />
            </Box>

            {loading ? (
                <CircularProgress />
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={selected.length === students.length && students.length > 0}
                                        onChange={handleSelectAll}
                                    />
                                </TableCell>
                                <TableCell>#</TableCell>
                                <TableCell>First Name</TableCell>
                                <TableCell>Last Name</TableCell>
                                <TableCell>Student ID</TableCell>
                                <TableCell>PIN Code</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {students.map((s, idx) => (
                                <TableRow key={s.id} hover>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selected.includes(s.id)}
                                            onChange={() => handleSelect(s.id)}
                                        />
                                    </TableCell>
                                    <TableCell>{idx + 1}</TableCell>
                                    <TableCell>{s.first_name}</TableCell>
                                    <TableCell>{s.last_name}</TableCell>
                                    <TableCell>{s.student_id || "-"}</TableCell>
                                    <TableCell>
                                        {showPin
                                            ? s.public_pin || "-"
                                            : s.public_pin
                                                ? "****"
                                                : "-"}
                                    </TableCell>
                                    <TableCell>
                                        {s.student_id && s.public_pin ? (
                                            <Typography color="success.main">✅ Credentials Exist</Typography>
                                        ) : (
                                            <Typography color="text.secondary">Not Set</Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You are about to delete public credentials for {selected.length} student(s).
                        They will no longer be able to access their dashboard. Are you sure?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button onClick={confirmDelete} color="error">Confirm Delete</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Loading State */}
            {loading && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        backdropFilter: "blur(8px)",
                        zIndex: 2000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Paper
                        elevation={8}
                        sx={{
                            p: { xs: 3, sm: 4 },
                            borderRadius: 4,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            backgroundColor: "white",
                            maxWidth: 400,
                            mx: 2
                        }}
                    >
                        <FloatingGradelyLoader
                            size={60}
                            showText={true}
                            text="Managing student public access credentials..."
                        />
                    </Paper>
                </Box>
            )}
        </Box>
    );
};

export default ManageStudentPublicAccess;
